'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import EducationHero from './EducationHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from EducationHero FEATURED + STATS. */
const METRICS = [
  { value: '$404B', label: 'Global education technology market by 2030 — growing at 16.5% CAGR (HolonIQ Global EdTech Report 2025)' },
  { value: '62%', label: "of prospective students say a brand's online reputation is a top-three factor in their enrolment decision (Qualtrics Higher Education Survey 2025)" },
  { value: '3 in 5', label: 'of EdTech users who experienced a trust issue — refund delay, course quality gap, or broken promise — shared that experience publicly (EDUCAUSE Student Experience Survey 2025)' },
];

export default function EducationBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('education');

  const CHALLENGES = [
    {
      title: t("industries_education.the_refund_complaint_flashpoint"),
      desc: t("industries_education.edtech_refund_complaint_posts_are_a"),
    },
    {
      title: t("industries_education.the_ed_influencer_blind_spot"),
      desc: t("industries_education.education_influencers_and_youtube_e"),
    },
    {
      title: t("industries_education.the_enrolment_window"),
      desc: t("industries_education.application_and_admission_seasons_a"),
    },
    {
      title: t("industries_education.the_silent_attrition_signal"),
      desc: t("industries_education.students_dont_always_escalate_loudl"),
    },
  ];

  const FAQS = [
    {
      q: t("industries_education.what_is_an_education_brand_reputati"),
      a: t("industries_education.one_workspace_for_student_complaint"),
    },
    {
      q: t("industries_education.how_does_locobuzz_help_edtech_brand"),
      a: t("industries_education.real_time_monitoring_fires_alerts_w"),
    },
    {
      q: t("industries_education.can_locobuzz_monitor_what_education"),
      a: t("industries_education.yes_paid_and_organic_educator_conte"),
    },
    {
      q: t("industries_education.how_does_locobuzz_help_universities"),
      a: t("industries_education.enrolment_season_monitoring_pre_con"),
    },
    {
      q: t("industries_education.can_locobuzz_track_competitive_inte"),
      a: t("industries_education.yes_competitor_platform_sentiment_c"),
    },
    {
      q: t("industries_education.how_does_locobuzz_support_k_12_scho"),
      a: t("industries_education.full_education_ecosystem_coverage_e"),
    },
  ];

  const BG_TITLE = t('industries_education.why_is_student_trust_now_the_most_c');
  const BG_ACCENT = 'an EdTech Brand or University Manages?';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">
        {t("industries_education.locobuzz_is_an_ainative_brand_reput")}
      </span>
      <span className="sr-only">
        {t("industries_education.students_and_parents_research_on_yo")}
      </span>
      <span className="sr-only">
        {t("industries_education.locobuzz_maps_six_purposebuilt_capa")}
      </span>
      <span className="sr-only">
        {t("industries_education.education_brands_using_locobuzz_rep")}
      </span>

      <EducationHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_education.why_student_trust_is_now_an_edtech")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_education.the_competitive_landscape_makes_thi")}
        imageSrc="/industries/why/education.png"
        imageAlt={t("industries_education.education_brand_reputation_and_stud")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_education.what_brand_cx_challenges_do_edtech")}
        items={CHALLENGES}
        layout="row5"
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="education-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
